/**
 * `meta.warnings` and `meta.notes` from every response, keyed by request
 * path, so the layout can show them once (WarningsBar) and charts can show
 * their own. Warnings say something is missing or estimated; notes say how
 * the figures were made, and stay out of the way.
 */
import { create } from "zustand";

export interface WarningEntry {
  path: string;
  warnings: string[];
  notes: string[];
  at: number;
}

interface WarningsState {
  byPath: Record<string, WarningEntry>;
  report: (path: string, warnings: string[], notes?: string[]) => void;
  clear: (path?: string) => void;
}

export const useWarnings = create<WarningsState>((set) => ({
  byPath: {},
  report: (path, warnings, notes = []) =>
    set((s) => {
      if (warnings.length === 0 && notes.length === 0) {
        if (!(path in s.byPath)) return s;
        const next = { ...s.byPath };
        delete next[path];
        return { byPath: next };
      }
      const prev = s.byPath[path];
      if (prev && same(prev.warnings, warnings) && same(prev.notes, notes)) return s;
      return { byPath: { ...s.byPath, [path]: { path, warnings, notes, at: Date.now() } } };
    }),
  clear: (path) =>
    set((s) =>
      path
        ? { byPath: Object.fromEntries(Object.entries(s.byPath).filter(([k]) => k !== path)) }
        : { byPath: {} },
    ),
}));

function same(a: string[], b: string[]): boolean {
  return a.length === b.length && a.every((w, i) => w === b[i]);
}

/** Distinct texts of one kind across entries, in first-seen order. */
export function distinct(entries: WarningEntry[], kind: "warnings" | "notes"): string[] {
  const seen = new Set<string>();
  for (const e of entries) for (const w of e[kind]) seen.add(w);
  return [...seen];
}

/** Distinct warning texts across all recent responses. */
export function useDistinctWarnings(): string[] {
  const byPath = useWarnings((s) => s.byPath);
  return distinct(Object.values(byPath), "warnings");
}

/** Distinct note texts across all recent responses. */
export function useDistinctNotes(): string[] {
  const byPath = useWarnings((s) => s.byPath);
  return distinct(Object.values(byPath), "notes");
}
